/**
 * PromptFormRegistry
 * Maps AI content type slugs to their custom prompt form components.
 * Modeled after content-kit's ElementRegistry singleton pattern.
 */

import type { PromptFormComponent } from './types';

const registry = new Map<string, PromptFormComponent>();

/**
 * Register a prompt form component for one or more AI content type slugs.
 */
export function registerPromptForm(
    slugOrSlugs: string | string[],
    component: PromptFormComponent
): void {
    const slugs = Array.isArray(slugOrSlugs) ? slugOrSlugs : [slugOrSlugs];
    for (const slug of slugs) {
        registry.set(slug, component);
    }
}

/**
 * Get the registered prompt form component for a given slug.
 * Returns null if no custom form is registered (caller should fall back).
 */
export function getPromptForm(slug: string): PromptFormComponent | null {
    return registry.get(slug) ?? null;
}

/**
 * Check whether a custom prompt form is registered for a slug.
 */
export function hasPromptForm(slug: string): boolean {
    return registry.has(slug);
}

/**
 * Get all registered slug keys (useful for debugging / tooling).
 */
export function getRegisteredSlugs(): string[] {
    return Array.from(registry.keys());
}
